import React, { Component } from 'react'
import ReactDOM from 'react-dom'
import { createValue, Fieldset, withFormValue, ErrorList, update } from '@bigfishtv/react-forms'
import Textarea from 'react-textarea-autosize'
import classnames from 'classnames'
import { validationErrors } from 'react-forms-cakephp'
import { Liquid } from 'liquidjs'
import { Button } from '@turretcss/react'

import invalidateExternalErrorList from './utils/invalidateExternalErrorList'

import FormButton from './components/FormButton'
import AddressInput from './common/AddressInput'

import ReactSelect from './components/ReactSelectLazy'

import RadioGroup from '@bigfishtv/cockpit/es5/components/input/RadioGroupInput'
import CheckboxGroup from '@bigfishtv/cockpit/es5/components/input/CheckboxGroup'
import SelectInput from '@bigfishtv/cockpit/es5/components/input/SelectInput'

import { loadFromLocalStorage, saveToLocalStorage } from './utils/localStorageUtils'
import { FileInput } from './components/FileInput'
import Upload, { isUploadUrl } from './components/Upload'

const $ = window.jQuery

function stringResultToBoolean(text) {
  text = text.trim().toLowerCase()
  return !(text === '' || text === 'false' || text === 'no' || text === '0')
}

function textToOptions(text) {
  return (text || '')
    .split(/\n/)
    .map((str) => str.trim())
    .filter((_) => _)
    .map((str) => ({ value: str, text: str }))
}

// assigns text value to new key
function keyChange(arr, key) {
  return arr.map((obj) => ({ value: obj.value, [key]: obj.text }))
}

function inlineOptions(text) {
  return (text || '').length < 30
}

/**
 * Generates a "safe" id to use for an array index if id doesn't exist.
 * This is used in module preview mode if the field/task hasn't been saved.
 *
 * @param  {int}   id
 * @param  {int}   i     The index of the item
 * @return {int}
 */
function safeId(id, i) {
  return id || 1000000000 - i
}

const requiredAsterisk = (
  <span
    style={{
      width: 11,
      height: 0,
      fontSize: 20,
      position: 'relative',
      top: -5,
      float: 'right',
      left: 3,
    }}
  >
    *
  </span>
)

const multiSelectStyles = {
  option: (provided) => ({
    ...provided,
    padding: '0px 10px',
    fontSize: '1.3rem',
  }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: '1.4rem',
    marginLeft: 0,
    marginRight: 0,
    letterSpacing: 0,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0 1.6rem',
  }),
  control: (provided, state) => ({
    alignItems: 'center',
    color: state.isFocused ? '#83878c' : '',
    background: state.isFocused ? '#fafafa' : '',
    border: state.isFocused ? '1px solid #008ed3' : '1px solid #eaebec',
    cursor: 'pointer',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    transition: 'all 100ms',
    boxSizing: 'border-box',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: '#000',
    fontSize: '1rem',
  }),
  multiValue: (provided) => ({
    ...provided,
    fontSize: '1.4rem',
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: '1.3rem',
    padding: '0.6rem 1.2rem',
    cursor: 'pointer',
    background: state.isFocused ? '#008ed3' : '',
    color: state.isFocused ? '#fff' : '#83878c',
  }),
  input: (provided) => ({
    ...provided,
    height: '4.4rem',
    margin: 0,
    paddingBottom: 0,
    paddingTop: 0,
    fontSize: '1.4rem',
  }),
  menu: (provided) => ({
    ...provided,
    fontSize: '1.4rem',
  }),
}

const multiSelectplaceholder = 'Please select ...'

const Field = withFormValue((props) => {
  const { title, label, name, instructions, placeholder, type, required } = props.field
  const options = props.field.preset ? props.field.preset.options : props.field.options
  const { formValue, fieldGroup } = props
  const errors = formValue.errorList
  const className = errors.length ? 'error' : ''
  const addDummyHeading = !title && fieldGroup && fieldGroup[0].title && '\u00A0'

  return (
    <div className="margin-top-medium margin-bottom-medium">
      {title && <h3 className="margin-bottom-medium">{title}</h3>}
      {addDummyHeading && <h3 className="margin-bottom-medium hidden-xsmall">&nbsp;</h3>}
      {label && (
        <label>
          {label} {required && requiredAsterisk}
        </label>
      )}
      {type == 'radiobuttons' && (
        <fieldset
          className={classnames('background-white', {
            error: errors.length > 0,
          })}
        >
          {instructions && <div className="instructions" dangerouslySetInnerHTML={{ __html: instructions }} />}
          <RadioGroup
            options={textToOptions(options)}
            inline={inlineOptions(options)}
            value={formValue.value}
            onChange={(value) => formValue.update(value)}
          />
        </fieldset>
      )}
      {type == 'checkboxes' && (
        <fieldset
          className={classnames('background-white', {
            error: errors.length > 0,
            'checkbox-agree': name === 'agree',
          })}
        >
          {instructions && <div className="instructions" dangerouslySetInnerHTML={{ __html: instructions }} />}
          <CheckboxGroup
            options={textToOptions(options)}
            inline={inlineOptions(options)}
            value={formValue.value}
            onChange={(value) => formValue.update(value)}
          />
        </fieldset>
      )}
      {type == 'dropdown' && (
        <SelectInput
          options={textToOptions(options)}
          value={formValue.value}
          onChange={(value) => formValue.update(value)}
          className={className}
        />
      )}
      {type == 'textarea' && (
        <Textarea
          minRows={3}
          value={formValue.value || ''}
          placeholder={placeholder}
          onChange={(event) => formValue.update(event.target.value)}
          className={className}
        />
      )}
      {type == 'text' && (
        <input
          value={formValue.value || ''}
          placeholder={placeholder}
          onChange={(event) => formValue.update(event.target.value)}
          className={className}
        />
      )}
      {type == 'number' && (
        <input
          type={type}
          value={formValue.value || ''}
          placeholder={placeholder}
          onChange={(event) => formValue.update(event.target.value)}
          className={className}
        />
      )}
      {type == 'email' && (
        <input
          type={type}
          value={formValue.value || ''}
          placeholder={placeholder}
          onChange={(event) => formValue.update(event.target.value)}
          className={className}
        />
      )}
      {type == 'tel' && (
        <input
          type={type}
          value={formValue.value || ''}
          placeholder={placeholder}
          onChange={(event) => formValue.update(event.target.value)}
          className={className}
        />
      )}
      {type == 'url' && (
        <input
          type={type}
          value={formValue.value || ''}
          placeholder={placeholder || 'https://...'}
          onChange={(event) => formValue.update(event.target.value)}
          className={className}
        />
      )}
      {type == 'date' && (
        <input
          type={type}
          value={formValue.value || ''}
          placeholder="YYYY-MM-DD"
          onChange={(event) => formValue.update(event.target.value)}
          className={className}
        />
      )}
      {type == 'datetime-local' && (
        <input
          type={type}
          value={formValue.value || ''}
          placeholder="YYYY-MM-DD HH:MM"
          onChange={(event) => formValue.update(event.target.value)}
          className={className}
        />
      )}
      {type == 'address' && (
        <AddressInput
          value={formValue.value ? { address: formValue.value } : null}
          onChange={(address) => formValue.update((address && address.address) || null)}
          placeholder={placeholder}
        />
      )}
      {type == 'file' && (
        <fieldset className={classnames('background-white', { error: errors.length > 0 })}>
          {instructions && <div className="margin-bottom-small" dangerouslySetInnerHTML={{ __html: instructions }} />}
          <FileInput
            value={formValue.value}
            onChange={(value) => formValue.update(value)}
            accept="image/jpeg,image/png,image/gif,application/pdf,text/plain,application/rtf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
          >
            <Button className="button-small" color="primary">
              Choose File
            </Button>
          </FileInput>
        </fieldset>
      )}
      {type == 'multiselect' && (
        <ReactSelect
          placeholder={multiSelectplaceholder}
          options={keyChange(textToOptions(options), 'label')}
          isMulti
          value={(formValue.value || []).map((value) => ({ label: value, value }))}
          onChange={(value) => {
            let a = (value || []).map((obj) => obj.value)
            formValue.update(!a.length ? null : a)
          }}
          styles={multiSelectStyles}
          className={classnames('multi-select-control', {
            error: errors.length > 0,
          })}
        />
      )}
      {type == 'hidden' && (
        <input
          value={formValue.value}
          onChange={(event) => formValue.update(event.target.value)}
          className={className}
        />
      )}
      {type == 'readonly' &&
        (isUploadUrl(formValue.value) ? (
          <div style={{ maxWidth: '25rem' }}>
            <Upload upload={formValue.value} link />
          </div>
        ) : (
          <input value={formValue.value || ''} placeholder={placeholder} className={className} disabled />
        ))}
      {errors.map((error, i) => (
        <div key={i} className="form-message error">
          {error.message}
        </div>
      ))}
      {instructions && type !== 'radiobuttons' && type !== 'checkboxes' && type !== 'file' && (
        <div className="instructions" dangerouslySetInnerHTML={{ __html: instructions }} />
      )}
    </div>
  )
})

function setFieldGroup(field, index, fieldGroup) {
  return update(
    field,
    ['component', 'props', 'fieldGroup'],
    fieldGroup.map((field) => field.component.props.field)
  )
}

function toNamedValues(fields, data) {
  return fields.reduce((acc, field) => {
    acc[field.name] = data[field.id] || null
    return acc
  }, {})
}

function fromNamedValues(fields, values) {
  return fields.reduce((acc, field) => {
    acc[field.id] = values[field.name]
    return acc
  }, {})
}

const autofillStorageKey = 'Form.autofill'

function saveAutofillValues(fields, data) {
  const values = toNamedValues(
    fields.filter((field) => field.autofill),
    data
  )
  const current = loadFromLocalStorage(autofillStorageKey, {})
  const merged = { ...current, ...values }
  saveToLocalStorage(autofillStorageKey, merged)
}

function loadAutofillValues(fields) {
  const current = loadFromLocalStorage(autofillStorageKey, {})
  return fromNamedValues(
    fields.filter((field) => field.autofill),
    current
  )
}

class Form extends Component {
  constructor(props) {
    super()
    this.state = {
      formValue: createValue({
        value: {
          ...loadAutofillValues(props.form.fields),
          ...(props.initialValue || {}),
          form_id: props.form.id,
        },
        onChange: this.onChange,
      }),
      submitStatus: 'ready',
    }
  }
  componentDidMount() {
    if (window.main && window.main.initialiseAfterLoad) {
      setTimeout(() => window.main.initialiseAfterLoad())
    }
  }
  onChange = (formValue) => {
    formValue = invalidateExternalErrorList(formValue, this.state.formValue)
    formValue = this.resetDisabledFields(formValue)
    this.setState({
      formValue,
      submitStatus: this.state.submitStatus != 'busy' ? 'ready' : 'busy',
    })
  }
  handleSubmit = (event) => {
    event.preventDefault()

    if (this.state.submitStatus == 'busy') return

    this.setState(
      {
        submitStatus: 'busy',
      },
      () => {
        $.ajax({
          type: 'POST',
          url: '/forms/submit.json',
          data: JSON.stringify(this.state.formValue.value),
          contentType: 'application/json',
          dataType: 'json',
          success: (data) => {
            saveAutofillValues(this.props.form.fields, data)

            if (this.props.form.after_submit === 'redirect' && this.props.form.success_redirect) {
              window.location.href = this.props.form.success_redirect.url
            } else if (this.props.form.after_submit === 'redirect_conditional' && this.props.form.redirect_conditions) {
              const namedValues = toNamedValues(this.props.form.fields, data)
              const engine = new Liquid()
              const tpl = engine.parse((this.props.form.global_variables || '') + this.props.form.redirect_conditions)
              const result = engine.renderSync(tpl, namedValues).trim()
              if (result) {
                window.location.href = result
              } else {
                this.setState({ submitStatus: 'success' })
              }
            } else {
              this.setState({ submitStatus: 'success' })
            }
          },
          error: (response) => {
            this.onChange(
              createValue({
                value: this.state.formValue.value,
                onChange: this.onChange,
                params: { forceShowErrors: true },
                externalErrorList: validationErrors(response.responseJSON.errors),
              })
            )
            this.setState({ submitStatus: 'failed' })
          },
        })
      }
    )
  }

  // these are caches for meetsConditions
  engine = null
  tpl = {}

  meetsConditions(field, namedValues) {
    if (!field.conditions) return true

    this.engine = this.engine || new Liquid()
    this.tpl[field.id] =
      this.tpl[field.id] || this.engine.parse((this.props.form.global_variables || '') + field.conditions)

    const result = this.engine.renderSync(this.tpl[field.id], namedValues)

    return stringResultToBoolean(result)
  }

  resetDisabledFields(formValue) {
    const namedValues = toNamedValues(this.props.form.fields, formValue.value)
    const disabledFields = this.props.form.fields.filter((field) => !this.meetsConditions(field, namedValues))
    // iterate through disabled fields and set them to null if they have a value
    const newValues = {}
    disabledFields.forEach((field) => {
      if (formValue.value[field.id]) newValues[field.id] = null
    })
    // if there are updates, return a new formValue
    if (Object.keys(newValues).length > 0) {
      const nextFormValue = formValue.createRoot({ value: { ...formValue.value, ...newValues } })
      // attempt to reset again in case there are complex conditions
      return this.resetDisabledFields(nextFormValue)
    }
    return formValue
  }

  render() {
    const { submitStatus } = this.state
    const { theme, fields, success_content } = this.props.form
    if (submitStatus === 'success') {
      return (
        <div
          ref={(el) => {
            // scroll the view to the section anchor
            $(el)
              .parents('section')
              .prev('.section-anchor')
              .each((i, anchor) => {
                anchor.scrollIntoView(true)
              })
          }}
        >
          {success_content ? (
            <div dangerouslySetInnerHTML={{ __html: success_content }} />
          ) : (
            <h3 className="padding-top-medium">Thanks for your submission!</h3>
          )}
        </div>
      )
    }

    const items = []

    let groupedFields = []

    const namedValues = toNamedValues(fields, this.state.formValue.value)

    fields.forEach((field, i) => {
      const nextField = i === fields.length - 1 ? null : fields[i + 1]
      const fieldComponent = (
        <Field select={safeId(field.id, i)} key={safeId(field.id, i)} field={field} theme={theme} />
      )
      let hasGroupedField = false

      if (field.type === 'hidden' || !this.meetsConditions(field, namedValues)) {
        return
      }

      if ((nextField && nextField.group_with_previous) || field.group_with_previous) {
        groupedFields.push({ key: fieldComponent.key, component: fieldComponent })
        hasGroupedField = true
      }
      if (groupedFields.length > 0 && (!nextField || !nextField.group_with_previous)) {
        items[field.order] = (
          <div
            className="inline-form margin-top-medium margin-bottom-medium"
            key={groupedFields[groupedFields.length - 1].key}
          >
            {groupedFields.map(setFieldGroup).map(({ component }) => component)}
          </div>
        )
        groupedFields = []
      }
      if (!hasGroupedField) {
        items[field.order] = fieldComponent
      }
    })

    this.props.sections.forEach((section) => {
      items[section.position] = (
        <div
          style={{ marginLeft: 'calc(50% - 50vw)', marginRight: 'calc(50% - 50vw)' }}
          key={'section' + section.position}
          dangerouslySetInnerHTML={{ __html: section.html }}
        />
      )
    })

    return (
      <Fieldset
        stylesheet={{ Root: 'form' }}
        formValue={this.state.formValue}
        onSubmit={!this.props.form.disable_submit ? this.handleSubmit : undefined}
      >
        {items}
        <div className="form-message error">
          <ErrorList select="remote_storage" />
        </div>
        {/* <DebugRedirect form={this.props.form} formValue={this.state.formValue} /> */}
        {!this.props.form.disable_submit && (
          <FormButton
            onSubmit={this.handleSubmit}
            submitStatus={submitStatus}
            submitClassName={theme === 'primary' ? 'button-dark' : undefined}
          />
        )}
      </Fieldset>
    )
  }
}

class DebugRedirect extends Component {
  getRedirectionUrl() {
    if (this.props.form.after_submit === 'redirect' && this.props.form.success_redirect) {
      return this.props.form.success_redirect.url || '[ show success message ]'
    } else if (this.props.form.after_submit === 'redirect_conditional') {
      const namedValues = toNamedValues(this.props.form.fields, this.props.formValue.value)
      const engine = new Liquid()
      const tpl = engine.parse((this.props.form.global_variables || '') + this.props.form.redirect_conditions)
      const result = engine.renderSync(tpl, namedValues).trim()
      return result || '[ show success message ]'
    }
    return '[ show success message ]'
  }

  render() {
    const redirectUrl = this.getRedirectionUrl()
    return <pre className="font-size-large">{redirectUrl}</pre>
  }
}

window.Form = {
  init: (id, props) => {
    ReactDOM.render(<Form {...props} />, document.getElementById(id))
  },
}
